import mixpanel from 'mixpanel-browser';
import type { Dict } from 'mixpanel-browser';

/** Group key — create a Mixpanel Group with this identifier to filter reports by tenant. */
export const MIXPANEL_TENANT_GROUP_KEY = 'tenant';

let initialized = false;
let activeTenantId: string | null = null;
let lastBootstrapKey: string | null = null;
let cachedConfig: Promise<{ token: string; apiHost?: string }> | null = null;

/** Same origin as axios when `VITE_API_BASE_URL` is set (split-host deploys); else relative to the page. */
function resolvePublicConfigUrl(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ?? '';
  if (!raw) return '/api/config/public';
  const base = raw.replace(/\/+$/, '');
  if (base.endsWith('/api')) return `${base}/config/public`;
  return `${base}/api/config/public`;
}

function runsInsideCrossOriginIframe(): boolean {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    return true;
  }
}

async function resolveMixpanelConfig(): Promise<{ token: string; apiHost?: string }> {
  if (!cachedConfig) {
    cachedConfig = loadMixpanelConfig();
  }
  return cachedConfig;
}

/**
 * Loads the Mixpanel project token: first from build-time env, otherwise from `/api/config/public`
 * so Railway/backend-only deployments work when `VITE_MIXPANEL_TOKEN` was not injected at build.
 */
async function loadMixpanelConfig(): Promise<{ token: string; apiHost?: string }> {
  const viteToken =
    typeof import.meta !== 'undefined'
      ? (import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined)?.trim() ?? ''
      : '';
  const viteHost =
    typeof import.meta !== 'undefined'
      ? (import.meta.env.VITE_MIXPANEL_API_HOST as string | undefined)?.trim() ?? ''
      : '';

  if (viteToken) {
    return { token: viteToken, apiHost: viteHost || undefined };
  }

  try {
    const res = await fetch(resolvePublicConfigUrl(), {
      mode: 'cors',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return { token: '' };
    const data = (await res.json()) as {
      mixpanelToken?: string | null;
      mixpanelApiHost?: string | null;
    };
    const token = (data.mixpanelToken ?? '').trim();
    const apiHost = (data.mixpanelApiHost ?? '').trim();
    return { token, apiHost: apiHost || undefined };
  } catch {
    return { token: '' };
  }
}

function toDict(props?: Record<string, unknown>): Dict {
  if (!props) return {};
  const out: Dict = {};
  for (const [key, raw] of Object.entries(props)) {
    if (raw === undefined || raw === null) continue;
    const t = typeof raw;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      out[key] = raw;
    } else {
      out[key] = String(raw);
    }
  }
  return out;
}

export function initAnalytics(projectToken: string, apiHost?: string): void {
  if (!projectToken || initialized) return;
  const inIframe = runsInsideCrossOriginIframe();
  const common = {
    track_pageview: false as const,
    persistence: 'localStorage' as const,
    ...(inIframe ? { disable_persistence: true as const } : {}),
    ...(apiHost ? { api_host: apiHost } : {}),
  };
  try {
    mixpanel.init(projectToken, common);
  } catch {
    mixpanel.init(projectToken, {
      ...common,
      disable_persistence: true,
    });
  }
  initialized = true;
}

export type AnalyticsBootstrapOptions = {
  tenantId?: string | null;
  userId?: string | null;
  locale?: string;
  sessionKey?: string;
};

/**
 * Initializes Mixpanel from Vite env or `/api/config/public`, registers tenant super-properties,
 * and tracks `app_opened`. Safe no-op when no token is configured.
 */
export async function bootstrapAppAnalytics(options: AnalyticsBootstrapOptions = {}): Promise<void> {
  const { token, apiHost } = await resolveMixpanelConfig();
  if (!token) return;

  const key = options.sessionKey ?? 'default';
  if (lastBootstrapKey === key) return;
  lastBootstrapKey = key;

  initAnalytics(token, apiHost);

  const tenantId = (options.tenantId ?? '').trim();
  if (tenantId) {
    activeTenantId = tenantId;
    mixpanel.register({
      tenant_id: tenantId,
      ...(options.locale ? { locale: options.locale } : {}),
    });
    mixpanel.set_group(MIXPANEL_TENANT_GROUP_KEY, tenantId);
  }

  const distinctId = (options.userId ?? '').trim();
  if (distinctId) {
    mixpanel.identify(distinctId);
  }

  const eventProps = toDict({
    tenant_id: tenantId || undefined,
    locale: options.locale,
  });

  if (tenantId) {
    mixpanel.track_with_groups('app_opened', eventProps, {
      [MIXPANEL_TENANT_GROUP_KEY]: tenantId,
    });
  } else {
    mixpanel.track('app_opened', eventProps);
  }
}

/** Session created from landing CTA (allowed before consent). */
export function trackIntakeStarted(properties?: { locale?: string; source?: string }): boolean {
  return trackEvent('intake_started', properties);
}

/** Fired when user completes a questionnaire step (before consent gate). */
export function trackQuestionnaireStepCompleted(properties: {
  step_id: number;
  venture_type?: string;
}): boolean {
  return trackEvent('questionnaire_step_completed', properties);
}

/** Fired only after user accepts FR-Q4 consent on the final step. */
export function trackConsentAccepted(properties: { consent_version: string }): boolean {
  return trackEvent('consent_accepted', properties);
}

/** Product events (after bootstrap). Requires successful Mixpanel init. */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): boolean {
  if (!initialized) return false;

  if (activeTenantId) {
    mixpanel.track_with_groups(
      eventName,
      toDict({ ...properties, tenant_id: activeTenantId }),
      { [MIXPANEL_TENANT_GROUP_KEY]: activeTenantId },
    );
  } else {
    mixpanel.track(eventName, toDict(properties));
  }
  return true;
}

/** Reset cached config — useful in tests. */
export function resetAnalyticsForTests(): void {
  initialized = false;
  activeTenantId = null;
  lastBootstrapKey = null;
  cachedConfig = null;
}
